import type { OptionDB } from "@project/emerge";
import { Sides } from "@project/emerge-types";
import type { SunlabCaseDefinition } from "@project/shared";
import filter from "lodash/filter";
import find from "lodash/find";
import { Vector3 } from "three";
import { AddableType } from "../enum/AddableType";
import { ConnectorCategory } from "../enum/ConnectorType";
import { Covers } from "../enum/Covers";
import { HorizontalAlignment } from "../enum/HorizontalAlignment";
import { InteriorType } from "../enum/InteriorType";
import { LTCaseUserOption } from "../enum/LTCaseUserOption";
import { LTProducts } from "../enum/LTProducts";
import { ReportEntryType } from "../enum/ReportEntryType";
import { ROLLER_TYPE } from "../enum/ROLLER_TYPE";
import { SunlabsCaseType } from "../enum/SunlabsCaseType";
import { SunlabsCaseVariant } from "../enum/SunlabsCaseVariant";
import { SunlabsConnectorType } from "../enum/SunlabsConnectorType";
import { SunlabsCount } from "../enum/SunlabsCount";
import { SunlabsSides } from "../enum/SunlabsSides";
import { VariableOptionType } from "../enum/VariableOptionType";
import { VerticalAlignment } from "../enum/VerticalAlignment";
import {
  createSingleItemTree,
  createTrivialSolution,
} from "../genetic_optimize/ContentTree";
import type { AngleSectionType } from "../interfaces/AngleSectionType";
import type { ContentItem } from "../interfaces/ContentItem";
import type { CornerBraceType } from "../interfaces/CornerBrace";
import type { FlightCaseSettings } from "../interfaces/FlightCaseSettings";
import type { LTCasePreset } from "../interfaces/LTCasePreset";
import type { PaddingType } from "../interfaces/Padding";
import type { PanelStrength } from "../interfaces/PanelStrength";
import type { ProfileType } from "../interfaces/ProfileType";
import type { RackRailType } from "../interfaces/RackRailType";
import type { CreeperRollerType } from "../interfaces/RollerBoardProperties";
import type { VariableOption } from "../interfaces/VariableOption";
import { manufacturer } from "../LTCase";
import {
  parseSunlabsString,
  sunlabSide2SidesAndMirror,
} from "../SunlabsCaseDefinition";
import { createAddableGrid } from "../structure/AddableInfos";
import { createConnectorSet } from "../structure/ConnectorSetInfos";
import {
  createCoverOptions,
  createRack19Interior,
  createRackInterior,
} from "../structure/RackInterior";
import { createRollerBoard } from "../structure/RollerBoardInfos";
import { createRunners } from "../structure/RunnerInfos";
import type { VariableOptions } from "../types/VariableOptions";
import type { LTCaseUserOptions } from "../user_options/all";
import { createEqualPaddingMap } from "../user_options/InteriorOptions";
import type { RollerBoardTypes } from "../user_options/RollerType";
import {
  getConfigurationTagsFromMegacaseInfo,
  type RequiredPreset,
} from "./getConfigurationTagsFromMegacaseInfo";

export const ReportEntryTypeDesc = {
  [ReportEntryType.ERROR]: {
    color: "danger",
    desc: "Fehler",
    level: 3,
  },
  [ReportEntryType.WARNING]: {
    color: "warning",
    desc: "Warnungen",
    level: 2,
  },
  [ReportEntryType.INFO]: {
    color: "info",
    desc: "Informationen",
    level: 1,
  },
};

export function getMaxReportType(
  report: ReportEntry[],
): ReportEntryType | undefined {
  return report.reduce(
    (acc: ReportEntryType | undefined, val: ReportEntry): ReportEntryType => {
      if (acc === undefined) {
        return val.type;
      } else {
        const oldLevel = ReportEntryTypeDesc[acc].level;
        const newLevel = ReportEntryTypeDesc[val.type].level;
        return oldLevel > newLevel ? acc : val.type;
      }
    },
    undefined,
  );
}

export function getCaseType(caseDefinition: SunlabCaseDefinition) {
  const caseTypeRegexp = /([A-Z_]+)_([A-Z]+)/;
  const result = caseTypeRegexp.exec(caseDefinition.Type) as
    | [string, SunlabsCaseType, SunlabsCaseVariant]
    | null;
  if (result === null) {
    throw new Error(`Invalid CaseType`);
  }
  const [, caseType, caseVariant] = result;
  return { caseType, caseVariant };
}

export const sunlabsCaseType2EmergyProductType: {
  [sunlabsName: string]: LTProducts;
} = {
  [SunlabsCaseType.KEYBOARDCASE]: LTProducts.KeyboardCase,
  [SunlabsCaseType.LID]: LTProducts.Haube,
  [SunlabsCaseType.RACK_DOUBLE_DOOR]: LTProducts.DoubleDoor,
  [SunlabsCaseType.RACK_SINGLE_DOOR]: LTProducts.SingleDoor,
  [SunlabsCaseType.SUITCASE]: LTProducts.Koffer,
  [SunlabsCaseType.TRUNK]: LTProducts.Truhe,
};

export interface ReportEntry {
  message: string;
  type: ReportEntryType;
}

type HandlePart = { number: number; side: string };

const HANDLE_COUNT_BY_NAME: Record<string, number> = {
  Four: 4,
  One: 1,
  Three: 3,
  Two: 2,
};

export type ImportReport = ReportEntry[];
export interface ImportResult {
  caseId: number;
  content?: ContentItem;
  report: ImportReport;
  requiredPresets?: RequiredPreset[];
  serializedProduct?: FlightCaseSettings;
  success: boolean;
}

const ROLLER_MOUNT_SIDE_SUFFIXES = new Set([
  "links",
  "rechts",
  "hinten",
  "vorne",
]);

export class CaseFactoryImporter {
  private readonly optionDB: OptionDB<LTCaseUserOptions>;
  private readonly presets: Array<LTCasePreset>;
  private report: ImportReport = [];

  constructor(
    optionDB: OptionDB<LTCaseUserOptions>,
    presets: Array<LTCasePreset> = [],
  ) {
    this.optionDB = optionDB;
    this.presets = presets;
  }

  public static getType(caseDefinition: SunlabCaseDefinition) {
    const { caseType } = getCaseType(caseDefinition);
    return sunlabsCaseType2EmergyProductType[caseType];
  }

  public *loadDish(
    config: string,
    partSelection: string,
    caseType: SunlabsCaseType,
    caseVariant: SunlabsCaseVariant,
  ) {
    const accTypeMatch = new RegExp(/(LabelNotch|VentilationDish)_.*/).exec(
      config,
    );
    if (!accTypeMatch?.[1]) {
      throw new Error(`loadDish: unrecognized config "${config}"`);
    }
    switch (accTypeMatch[1]) {
      case "LabelNotch": {
        // Tourlabel
        yield createAddableGrid(
          this.optionDB,
          AddableType.Dish,
          Sides.TOP,
          partSelection,
          {
            orientation: 0,
            verticalAlignment: VerticalAlignment.BOTTOM,
            verticalMargin: 123,
          },
        );
        break;
      }
      case "VentilationDish": {
        // Belüftungsschale
        let bHeight = 0; // ?? no vents for others afaik
        if (caseType === SunlabsCaseType.TRUNK) {
          if (caseVariant === SunlabsCaseVariant.HEAVY) {
            bHeight = 90;
          }
          if (caseVariant === SunlabsCaseVariant.PROFESSIONAL) {
            bHeight = 77;
          }
          if (caseVariant === SunlabsCaseVariant.LIVE) {
            bHeight = 63.5;
          }
        }
        let margin = 50;
        if (caseType === "LID") {
          margin = 60;
        }
        yield createAddableGrid(
          this.optionDB,
          AddableType.Dish,
          Sides.LEFT,
          partSelection,
          {
            mirror: true,
            orientation: 0,
            verticalAlignment: VerticalAlignment.TOP,
            verticalMargin: bHeight + margin,
          },
        );
        break;
      }
    }
  }
  public *loadHandle(
    config: string,
    partSelection: string,
    caseType: SunlabsCaseType,
    caseVariant: SunlabsCaseVariant,
  ) {
    if (config === "Normal_OneBottomBodyAndOneTopCap") {
      yield createAddableGrid(
        this.optionDB,
        AddableType.Handle,
        Sides.TOP,
        partSelection,
        {
          horizontalAlignment: HorizontalAlignment.RIGHT,
          horizontalMargin: 350,
          mirror: true,
          verticalAlignment: VerticalAlignment.BOTTOM,
          verticalMargin: 159,
        },
      );
      return;
    }

    if (config === "Normal_AsTrolleyFunction") {
      yield createAddableGrid(
        this.optionDB,
        AddableType.Handle,
        Sides.LEFT,
        partSelection,
        {
          horizontalMargin: 30,
          verticalMargin: 0,
        },
      );
      return;
    }

    const parts = this.parseHandleParts(config);
    if (parts === null) {
      return;
    }

    for (const part of parts) {
      yield* this.createHandleOptionsForPart(
        part,
        partSelection,
        caseType,
        caseVariant,
      );
    }
  }

  private parseHandleParts(config: string): HandlePart[] | null {
    const m = new RegExp(
      /^Normal_(One|Two)(Front|FrontBack|LeftRight|Top|Bottom|Left)(?:And(One)?(Front|LeftRight|Top))?(Body|Cap)(?:And(One)(Front|Top)(Cap))?$/,
    ).exec(config);

    if (m === null) {
      return null;
    }

    const [, count, side, count2, side2, , count3, side3] = m;
    if (count === undefined || side === undefined) {
      throw new Error(`loadHandle: invalid config "${config}"`);
    }

    const parts: HandlePart[] = [{ number: this.handleCount(count), side }];

    if (side2 !== undefined) {
      parts.push({
        number: this.handleCount(count2 ?? count),
        side: side2,
      });
    }

    if (side3 !== undefined) {
      if (count3 === undefined) {
        throw new Error(`loadHandle: invalid config "${config}"`);
      }
      parts.push({ number: this.handleCount(count3), side: side3 });
    }

    return parts;
  }

  private handleCount(count: string): number {
    const value = HANDLE_COUNT_BY_NAME[count];
    if (value === undefined) {
      throw new Error(`loadHandle: invalid handle count "${count}"`);
    }
    return value;
  }

  private *createHandleOptionsForPart(
    part: HandlePart,
    partSelection: string,
    caseType: SunlabsCaseType,
    caseVariant: SunlabsCaseVariant,
  ): IterableIterator<VariableOptions> {
    const { side, mirror } = sunlabSide2SidesAndMirror(
      part.side as SunlabsSides,
    );

    if (part.number === 1) {
      yield createAddableGrid(
        this.optionDB,
        AddableType.Handle,
        side,
        partSelection,
        {
          mirror,
          orientation: 0,
          verticalAlignment: VerticalAlignment.CENTER,
        },
      );
      return;
    }

    if (part.number === 2) {
      yield* this.createDoubleHandleOptions(
        side,
        mirror,
        partSelection,
        caseType,
        caseVariant,
      );
    }
  }

  private *createDoubleHandleOptions(
    side: Sides,
    mirror: boolean,
    partSelection: string,
    caseType: SunlabsCaseType,
    caseVariant: SunlabsCaseVariant,
  ): IterableIterator<VariableOptions> {
    if (side === Sides.FRONT) {
      yield* this.createFrontDoubleHandleOptions(
        side,
        mirror,
        partSelection,
        caseType,
        caseVariant,
      );
      return;
    }

    if (side === Sides.LEFT) {
      const horizontalMargin =
        caseVariant === SunlabsCaseVariant.HEAVY ? 60 : 50;
      yield createAddableGrid(
        this.optionDB,
        AddableType.Handle,
        side,
        partSelection,
        {
          horizontalCount: 2,
          horizontalMargin,
          mirror,
          verticalMargin: 0,
        },
      );
    }
  }

  private *createFrontDoubleHandleOptions(
    side: Sides,
    mirror: boolean,
    partSelection: string,
    caseType: SunlabsCaseType,
    caseVariant: SunlabsCaseVariant,
  ): IterableIterator<VariableOptions> {
    const { dY, bHeight } = this.getFrontHandleOffsets(caseVariant);

    if (caseType === SunlabsCaseType.LID) {
      yield createAddableGrid(
        this.optionDB,
        AddableType.Handle,
        side,
        partSelection,
        {
          horizontalCount: 2,
          horizontalMargin: 80,
          mirror,
          verticalAlignment: VerticalAlignment.BOTTOM,
          verticalMargin: dY + bHeight,
        },
      );
    }

    if (caseType === SunlabsCaseType.TRUNK) {
      const horizontalMargin =
        caseVariant === SunlabsCaseVariant.PROFESSIONAL ? 150 : 140;
      yield createAddableGrid(
        this.optionDB,
        AddableType.Handle,
        side,
        partSelection,
        {
          horizontalCount: 2,
          horizontalMargin,
          mirror,
          verticalAlignment: VerticalAlignment.TOP,
          verticalMargin: dY + bHeight,
        },
      );
    }
  }

  private getFrontHandleOffsets(caseVariant: SunlabsCaseVariant): {
    dY: number;
    bHeight: number;
  } {
    switch (caseVariant) {
      case SunlabsCaseVariant.LIVE:
        return { dY: 100, bHeight: 113.5 };
      case SunlabsCaseVariant.PROFESSIONAL:
        return { dY: 100, bHeight: 67 };
      case SunlabsCaseVariant.HEAVY:
        return { dY: 130, bHeight: 90 };
      default:
        return { dY: 89, bHeight: 0 };
    }
  }
  public *loadConnector(
    config: string,
    partSelection: string,
    caseType: SunlabsCaseType,
    variant: SunlabsCaseVariant,
    pianoHinge: boolean,
  ) {
    if (config.startsWith("PianoHinge")) {
      return;
    }

    const [type, count, sides] = parseSunlabsString<
      SunlabsConnectorType,
      SunlabsCount,
      SunlabsSides
    >(config, SunlabsConnectorType, "_", SunlabsCount, SunlabsSides);

    const numbers = {
      Four: 4,
      One: 1,
      Three: 3,
      Two: 2,
      Five: 5,
      Six: 6,
    };

    // if ((match == null)) {
    //     this.error(
    //         `Verbinder-Typ ${connector.HingeConfiguration} unbekannt. Verbinder bitte manuell hinzufügen!`
    //     );
    //     continue;
    // }

    const { side, mirror } = sunlabSide2SidesAndMirror(sides);
    let margin = 80;
    if (caseType === SunlabsCaseType.TRUNK) {
      if (variant === SunlabsCaseVariant.HEAVY) {
        margin = 140;
      }
      if (variant === SunlabsCaseVariant.PROFESSIONAL) {
        margin = 150;
      }
    }
    const connectorGroup =
      [SunlabsCaseType.KEYBOARDCASE, SunlabsCaseType.LID].indexOf(caseType) ===
      -1
        ? Covers.COVER
        : Covers.BASE;
    switch (type) {
      case SunlabsConnectorType.HingeForLidStay:
        yield createConnectorSet(
          this.optionDB,
          ConnectorCategory.Hinge,
          connectorGroup,
          side,
          partSelection,
          {
            count: 2,
            flip: false,
            margin: variant === SunlabsCaseVariant.HEAVY ? 85 : 95,
            mirror,
            pianoHinge,
          },
        );
        break;

      case SunlabsConnectorType.Detachable:
      case SunlabsConnectorType.Normal:
      case SunlabsConnectorType.Lock:
        yield createConnectorSet(
          this.optionDB,
          type === SunlabsConnectorType.Lock
            ? ConnectorCategory.Lock
            : ConnectorCategory.Hinge,
          connectorGroup,
          side,
          partSelection,
          {
            count: numbers[count],
            flip: false, // [SunlabsCaseType.LID, SunlabsCaseType.KEYBOARDCASE].includes(caseType),
            margin,
            mirror,
            pianoHinge,
          },
        );
        break;

      default:
        break;
    }
  }
  public *getVariableOptionForRequiredPreset(preset: RequiredPreset) {
    switch (preset.possibleTypes[0]) {
      case VariableOptionType.DISHES:
        for (const variableOption of this.loadDish(
          preset.configuration,
          preset.partSelection,
          preset.caseType,
          preset.caseVariant,
        )) {
          yield { ...variableOption, tags: [preset.configuration] };
        }
        break;
      case VariableOptionType.FEET:
        for (const variableOption of this.transformWheel(
          preset.configuration,
          preset.partSelection,
          preset.caseVariant,
        )) {
          yield { ...variableOption, tags: [preset.configuration] };
        }
        break;
      case VariableOptionType.HANDLES:
        for (const variableOption of this.loadHandle(
          preset.configuration,
          preset.partSelection,
          preset.caseType,
          preset.caseVariant,
        )) {
          yield { ...variableOption, tags: [preset.configuration] };
        }
        break;
      case VariableOptionType.HINGE_SET:
      case VariableOptionType.LOCK_SET:
        for (const variableOption of this.loadConnector(
          preset.configuration,
          preset.partSelection,
          preset.caseType,
          preset.caseVariant,
          preset.pianoHinge ?? false,
        )) {
          yield { ...variableOption, tags: [preset.configuration] };
        }
        break;
    }
  }
  public *transformWheel(
    config: string,
    name: string,
    variant: SunlabsCaseVariant,
  ): IterableIterator<VariableOptions> {
    const [type, _subtype] = config.split("_");
    switch (type) {
      case "Foot":
        yield* this.transformFootWheel(config, name);
        break;
      case "PulloutHandle":
        yield* this.transformStandardWheelAddon(
          config,
          name,
          AddableType.TrolleyHandle,
          `Ausziehgriff Untertyp ${config} für Rad ${name} unbekannt.`,
        );
        break;
      case "Slidebar":
        yield* this.transformSlidebar(config, name);
        break;
      case "WheelNotch":
        yield* this.transformWheelNotch(config, name, variant);
        break;
      case "Trolley":
        yield* this.transformTrolleyWheel(config, name);
        break;
    }
  }

  private *transformFootWheel(
    config: string,
    name: string,
  ): IterableIterator<VariableOptions> {
    if (config.includes("Standard")) {
      yield createAddableGrid(
        this.optionDB,
        AddableType.Foot,
        Sides.BOTTOM,
        name,
        {
          horizontalCount: 2,
          horizontalMargin: 10,
          verticalCount: 2,
          verticalMargin: 10,
        },
      );
      return;
    }

    if (
      config.includes("WithPulloutHandle") ||
      config.includes("WithCornerWheel")
    ) {
      yield createAddableGrid(
        this.optionDB,
        AddableType.Foot,
        Sides.BOTTOM,
        name,
        {
          horizontalAlignment: HorizontalAlignment.LEFT,
          horizontalMargin: 60,
          verticalCount: 2,
          verticalMargin: 60,
        },
      );
      yield createAddableGrid(
        this.optionDB,
        AddableType.Foot,
        Sides.RIGHT,
        name,
        {
          horizontalCount: 2,
          horizontalMargin: 60,
          verticalAlignment: VerticalAlignment.TOP,
          verticalMargin: 60,
        },
      );
      return;
    }

    if (config.includes("Foot_WithTrolleyFunction")) {
      yield createAddableGrid(
        this.optionDB,
        AddableType.Foot,
        Sides.RIGHT,
        name,
        {
          horizontalCount: 2,
          horizontalMargin: 100,
          verticalAlignment: VerticalAlignment.TOP,
          verticalMargin: 35,
        },
      );
      return;
    }

    if (config.includes("WithTrolleyFunction")) {
      yield createAddableGrid(
        this.optionDB,
        AddableType.Foot,
        Sides.BOTTOM,
        name,
        {
          horizontalCount: 2,
          horizontalMargin: 60,
          verticalCount: 2,
          verticalMargin: 60,
        },
      );
      yield createAddableGrid(
        this.optionDB,
        AddableType.Foot,
        Sides.RIGHT,
        name,
        {
          horizontalCount: 2,
          horizontalMargin: 60,
          verticalCount: 2,
          verticalMargin: 35,
        },
      );
      return;
    }

    throw new Error(`Fuß Untertyp ${config} für Rad ${name} unbekannt.`);
  }

  private *transformStandardWheelAddon(
    config: string,
    name: string,
    addableType: AddableType,
    errorMessage: string,
  ): IterableIterator<VariableOptions> {
    if (!config.includes("Standard")) {
      throw new Error(errorMessage);
    }
    yield createAddableGrid(this.optionDB, addableType, Sides.BOTTOM, name);
  }

  private *transformSlidebar(
    config: string,
    name: string,
  ): IterableIterator<VariableOptions> {
    if (!config.includes("Standard")) {
      throw new Error(
        `Rutschleiste Untertyp ${config} für Rad ${name} unbekannt.`,
      );
    }
    yield createRunners(name === "1005" ? 24 : 18);
  }

  private *transformWheelNotch(
    config: string,
    name: string,
    variant: SunlabsCaseVariant,
  ): IterableIterator<VariableOptions> {
    if (!config.includes("Standard")) {
      throw new Error(`Radmulde Untertyp ${config} für Rad ${name} unbekannt.`);
    }

    yield createAddableGrid(this.optionDB, AddableType.Dish, Sides.TOP, name, {
      horizontalCount: 2,
      horizontalMargin: 179,
      verticalCount: 2,
      verticalMargin: variant === SunlabsCaseVariant.HEAVY ? 78 : 76,
    });
  }

  private *transformTrolleyWheel(
    config: string,
    name: string,
  ): IterableIterator<VariableOptions> {
    if (!config.includes("Standard")) {
      throw new Error(
        `Trolley Untertyp ${config} für Rad ${name} (Trolley) unbekannt.`,
      );
    }
    yield createAddableGrid(
      this.optionDB,
      AddableType.TrolleyHandle,
      Sides.BOTTOM,
      name,
      { orientation: 180 },
    );
  }
  public go(caseDefinition: SunlabCaseDefinition): ImportResult {
    this.optionDB.resetErrors();
    this.report = [];
    this.success(
      `Import von Sunlab Case ID ${caseDefinition.CaseId} gestartet`,
    );
    if (caseDefinition.Type == null) {
      this.error(
        "Die Daten in diesem Auftrag sind unvollständig: kein Case-Typ angegeben!",
      );
      return {
        caseId: caseDefinition.CaseId,
        report: this.report,
        success: false,
      };
    }

    const { caseType, caseVariant } = getCaseType(caseDefinition);
    if (!sunlabsCaseType2EmergyProductType[caseType]) {
      this.error(
        `Kistentyp ${caseDefinition.Type} wird nicht unterstüzt. Import abgebrochen.`,
      );
      return {
        caseId: caseDefinition.CaseId,
        report: this.report,
        success: false,
      };
    }
    const isHood =
      caseType === SunlabsCaseType.LID ||
      caseType === SunlabsCaseType.KEYBOARDCASE;
    const productType = sunlabsCaseType2EmergyProductType[caseType];
    if (!this.hasRequiredSunlabData(caseDefinition)) {
      this.error(
        `Die Daten entsprechen nicht dem mit Sunlabs vereinbarten
            Standard und können daher nicht importiert werden.`,
      );
      return {
        caseId: caseDefinition.CaseId,
        report: this.report,
        success: false,
      };
    }

    if (caseDefinition.Sections.length > 1) {
      //  todo: this stopped working for 19` racks that have rack rails
      // M1384 and M1385 are the two sides of the same profile, so we don't need a warning if they are both given
      // if ((caseDefinition.Sections[0].Section !== "M1384") ||
      //   (caseDefinition.Sections[0].SectionConfiguration !== "CloseSection_Body") ||
      //   (caseDefinition.Sections[1].Section !== "M1385") ||
      //   (caseDefinition.Sections[1].SectionConfiguration !== "CloseSection_Cap")) {
      //   this.warn(
      //     "Es wurden mehrere Typen für das Schließprofil angegeben. Nur die erste Angabe wird berücksichtigt.",
      //   );
      // }
    }

    // change profile AH6303 to M6103 since LT does not use this profile
    if (
      caseDefinition.Sections[0]?.Section === "STW6303" ||
      caseDefinition.Sections[0]?.Section === "AH6303"
    ) {
      caseDefinition.Sections[0].Section = "STW6103";
      this.warn(
        `Schließprofil AH6303 (10mm Hybrid mit Dichtung) wird von LT nicht unterstützt.
           Es wurde das Profil 9,5 Hybrid (STW6103) als Ersatz gewählt.`,
      );
    }

    const serializedResult = manufacturer.products[
      productType
    ].getSerializedDefaultVersion(this.optionDB) as FlightCaseSettings;

    this.applyProductTypeOptions(serializedResult, caseType, caseDefinition);

    const cutHeight = this.cutHeightForVariant(caseVariant);
    for (const cut of serializedResult.cuts) {
      cut.offset = cutHeight;
    }

    const closingSection = caseDefinition.Sections[0];
    if (closingSection === undefined) {
      this.error(`Kein Schließprofil (Sections[0]) in den Sunlabs-Daten.`);
      return {
        caseId: caseDefinition.CaseId,
        report: this.report,
        success: false,
      };
    }

    this.applyExteriorOptions(
      serializedResult,
      caseDefinition,
      closingSection,
      isHood,
    );

    const content = this.applyInteriorOptions(
      serializedResult,
      caseDefinition,
      caseType,
      closingSection,
    );

    serializedResult.variableOptions.length = 0;
    this.applyRollerBoardOptions(serializedResult, caseDefinition);
    const requiredPresets =
      getConfigurationTagsFromMegacaseInfo(caseDefinition);

    this.applyRequiredPresets(serializedResult, requiredPresets);

    for (const connectorSet of this.loadConnectors(
      caseDefinition,
      caseType,
      caseVariant,
    )) {
      serializedResult.variableOptions.push(connectorSet);
    }
    this.applyRack19Options(
      serializedResult,
      caseDefinition,
      caseType,
      caseVariant,
    );
    for (const error of this.optionDB.getErrors()) {
      this.error(error);
    }

    this.success("Import beendet.");

    // Attach variable option from import (mirror, flip, alignment, side, group, count, margin, etc.)
    // so the UI can add the part with exact import values when no preset matches.
    const requiredPresetsWithImportOptions =
      this.attachImportOptionsToRequiredPresets(requiredPresets);

    return {
      caseId: caseDefinition.CaseId,
      content,
      report: this.report,
      requiredPresets: requiredPresetsWithImportOptions,
      serializedProduct: serializedResult,
      success: this.reportContainsOnlyInfo(),
    };

    // } catch (e) {
    //   this.error(e);
    //   return {
    //     report: this.report,
    //     success: false,
    //   };
    // }
  }

  private hasRequiredSunlabData(caseDefinition: SunlabCaseDefinition): boolean {
    const requiredValues = [
      caseDefinition.Sections,
      caseDefinition.Count,
      caseDefinition.Panel,
      caseDefinition.PanelThickness,
      caseDefinition.Width,
      caseDefinition.Height,
      caseDefinition.Depth,
      caseDefinition.CornerCap,
      caseDefinition.EdgeProtectionCap,
      caseDefinition.EdgeProtectionBody,
      caseDefinition.Fastener,
      caseDefinition.FastenerConfiguration,
      caseDefinition.Hinges,
      caseDefinition.Handles,
    ];
    const hasCornerBrace =
      caseDefinition.LCornerCap != null || caseDefinition.LCornerBody != null;
    return (
      requiredValues.every((value) => value != null) &&
      hasCornerBrace &&
      Array.isArray(caseDefinition.Sections) &&
      Array.isArray(caseDefinition.Hinges) &&
      Array.isArray(caseDefinition.Handles)
    );
  }

  private applyProductTypeOptions(
    serializedResult: FlightCaseSettings,
    caseType: SunlabsCaseType,
    caseDefinition: SunlabCaseDefinition,
  ): void {
    switch (caseType) {
      case SunlabsCaseType.LID:
        serializedResult.type = LTProducts.Haube;
        break;
      case SunlabsCaseType.RACK_DOUBLE_DOOR:
        serializedResult.type = LTProducts.DoubleDoor;
        break;
      case SunlabsCaseType.SUITCASE:
        serializedResult.type = LTProducts.Koffer;
        break;
      case SunlabsCaseType.TRUNK:
        serializedResult.type = LTProducts.Truhe;
        break;
      case SunlabsCaseType.KEYBOARDCASE:
        serializedResult.type = LTProducts.KeyboardCase;
        serializedResult.options = this.keyboardOptions(caseDefinition);
        break;
    }
  }

  private keyboardOptions(caseDefinition: SunlabCaseDefinition) {
    const {
      OptionAccessoriesCompartment,
      OptionAdditionalBackRackRail,
      OptionLeatherHandle,
      OptionPlayableInCase,
      OptionRemovableCap,
      OptionStackable,
      OptionTrolleyFunction,
    } = caseDefinition;
    return {
      OptionAccessoriesCompartment,
      OptionAdditionalBackRackRail,
      OptionLeatherHandle,
      OptionPlayableInCase,
      OptionRemovableCap,
      OptionStackable,
      OptionTrolleyFunction,
    };
  }

  private cutHeightForVariant(caseVariant: SunlabsCaseVariant): number {
    switch (caseVariant) {
      case SunlabsCaseVariant.HEAVY:
        return 72;
      case SunlabsCaseVariant.ECONOMY:
        return 32;
      default:
        return 50;
    }
  }

  private resolveClosingProfileType(
    closingSection: SunlabCaseDefinition["Sections"][number],
    isHood: boolean,
  ): ProfileType {
    return this.optionDB.getByName<ProfileType>(
      LTCaseUserOption.ProfileType,
      closingSection.Section +
        (isHood && closingSection.Section !== "STW1384" ? "i" : ""),
    );
  }

  /**
   * Sunlabs {@link SunlabCaseDefinition.Height} is the ordered interior usable dimension
   * (same convention as {@link SunlabCaseDefinition.Width} / {@link SunlabCaseDefinition.Depth}
   * on the import content item). Outer shell height is derived later by
   * {@link FlightCaseBlueprintCalculator}, not subtracted here.
   */
  private importedCaseContentHeightMm(
    caseDefinition: SunlabCaseDefinition,
  ): number {
    return Math.max(1, caseDefinition.Height);
  }

  private applyExteriorOptions(
    serializedResult: FlightCaseSettings,
    caseDefinition: SunlabCaseDefinition,
    closingSection: SunlabCaseDefinition["Sections"][number],
    isHood: boolean,
  ): void {
    serializedResult.exteriorOptions.profiles = this.resolveClosingProfileType(
      closingSection,
      isHood,
    );
    serializedResult.exteriorOptions.color = this.optionDB.getByName(
      LTCaseUserOption.PanelColor,
      caseDefinition.Panel,
    );
    const panelStrength = this.optionDB.getByName<PanelStrength>(
      LTCaseUserOption.PanelStrength,
      caseDefinition.PanelThickness.toString(),
    );
    serializedResult.exteriorOptions.strength = (
      panelStrength ? panelStrength.strength : 6.5
    ) as 9 | 6.5;
    serializedResult.exteriorOptions.angleSections =
      this.optionDB.getByName<AngleSectionType>(
        LTCaseUserOption.AngleSectionType,
        this.loadAngleSections(caseDefinition),
      );
    serializedResult.exteriorOptions.cornersTop = this.optionDB.getByName(
      LTCaseUserOption.CornerType,
      caseDefinition.CornerCap,
    );
    serializedResult.exteriorOptions.cornersBottom = this.optionDB.getByName(
      LTCaseUserOption.CornerType,
      caseDefinition.CornerBody || caseDefinition.CornerCap,
    );
    const importedCornerBraces =
      this.resolveImportedCornerBraces(caseDefinition);
    serializedResult.exteriorOptions.cornerBraces =
      importedCornerBraces.cornerBraces;
    serializedResult.exteriorOptions.cornerBracesCover =
      importedCornerBraces.cornerBracesCover;
    serializedResult.exteriorOptions.rollers =
      this.resolveExteriorRollerFromImport(caseDefinition);
  }

  private normRollerKey(value: string): string {
    return value.replaceAll(/\s/g, "");
  }

  /** Strip mount-side suffix so Sunlabs base names match unique catalog names. */
  private rollerCatalogBaseName(name: string): string {
    const trimmed = name.trimEnd();
    const lastSpace = trimmed.lastIndexOf(" ");
    if (lastSpace === -1) {
      return this.normRollerKey(name);
    }
    const mountSide = trimmed.slice(lastSpace + 1).toLowerCase();
    if (!ROLLER_MOUNT_SIDE_SUFFIXES.has(mountSide)) {
      return this.normRollerKey(name);
    }
    return this.normRollerKey(trimmed.slice(0, lastSpace).trimEnd());
  }

  private resolveExteriorRollerFromImport(
    caseDefinition: SunlabCaseDefinition,
  ): RollerBoardTypes {
    const selection = this.getRollerImportSelection(caseDefinition);
    if (selection.kind === "none" || selection.kind === "rollerBoard") {
      return this.findNoRoller();
    }
    return this.resolveExteriorRoller(selection.baseName, selection.side);
  }

  private resolveExteriorRoller(
    rollerName: string,
    side: Sides,
  ): RollerBoardTypes {
    const availableRollers = this.optionDB.getOptions<RollerBoardTypes>(
      LTCaseUserOption.RollerType,
    );
    const compactName = this.normRollerKey(rollerName);

    const found = availableRollers.find((r) => {
      if (
        r.rollerType === ROLLER_TYPE.CREEPER ||
        r.rollerType === ROLLER_TYPE.NONE
      ) {
        return false;
      }
      if (r.side !== side) return false;
      const names = [r.name, ...(r.aliases ?? [])].filter(
        (n): n is string => typeof n === "string",
      );
      return names.some(
        (n) =>
          this.normRollerKey(n) === compactName ||
          this.rollerCatalogBaseName(n) === compactName,
      );
    });
    if (found) return found;

    const availableRollerDescriptions = availableRollers
      .map((r) => r.name + " [side=" + r.side + "]")
      .join(", ");
    this.warn(
      `RollerType "${rollerName}" (side ${side}) nicht gefunden. Verfügbare Optionen: ${availableRollerDescriptions}`,
    );
    const fallback = this.optionDB.getByName(
      LTCaseUserOption.RollerType,
      rollerName,
    );
    return { ...fallback, side } as RollerBoardTypes;
  }

  private isRollerBoardName(rollersName: string): boolean {
    return (
      rollersName === "Rollbrett gebremst" ||
      rollersName === "Rollbrett ungebremst"
    );
  }

  private applyInteriorOptions(
    serializedResult: FlightCaseSettings,
    caseDefinition: SunlabCaseDefinition,
    caseType: SunlabsCaseType,
    closingSection: SunlabCaseDefinition["Sections"][number],
  ): ContentItem | undefined {
    if (
      caseType === SunlabsCaseType.RACK_DOUBLE_DOOR ||
      caseType === SunlabsCaseType.RACK_SINGLE_DOOR
    ) {
      serializedResult.interiorOptions = this.createRackInteriorOptions(
        caseDefinition,
        serializedResult.exteriorOptions.strength,
      );
      return undefined;
    }

    return this.applyCaseInteriorOptions(
      serializedResult,
      caseDefinition,
      caseType,
      closingSection,
    );
  }

  private createRackInteriorOptions(
    caseDefinition: SunlabCaseDefinition,
    strength: 9 | 6.5,
  ) {
    if (caseDefinition.Width === 483) {
      return createRack19Interior(
        caseDefinition.HeightUnits === null
          ? undefined
          : caseDefinition.HeightUnits,
        caseDefinition.Depth,
        strength,
      );
    }
    return createRackInterior(
      caseDefinition.Height,
      caseDefinition.Depth,
      caseDefinition.Width,
    );
  }

  private applyCaseInteriorOptions(
    serializedResult: FlightCaseSettings,
    caseDefinition: SunlabCaseDefinition,
    caseType: SunlabsCaseType,
    closingSection: SunlabCaseDefinition["Sections"][number],
  ): ContentItem {
    const { padding, paddingTop, paddingAlignment, tolerance } =
      this.loadPadding(caseDefinition);
    const contentHeightMm = this.importedCaseContentHeightMm(caseDefinition);
    const content = this.createImportContent(
      caseDefinition,
      padding,
      contentHeightMm,
    );
    const toleranceIncrement = this.profileToleranceIncrement(closingSection);

    serializedResult.interiorOptions = createTrivialSolution(
      content,
      new Vector3(7, tolerance + toleranceIncrement, 7),
      this.optionDB.getByName(LTCaseUserOption.TrayMaterialType, "PLW1415"),
      paddingAlignment,
    );
    serializedResult.coverPaddings = createCoverOptions(
      serializedResult.cuts,
      paddingTop.name,
      caseType === SunlabsCaseType.LID ||
        caseType === SunlabsCaseType.KEYBOARDCASE
        ? padding.name
        : "nopad",
    );
    serializedResult.interiorOptions = {
      tolerance: { x: 7, y: tolerance + toleranceIncrement, z: 7 },
      trayMaterial: this.optionDB.getByName(
        LTCaseUserOption.TrayMaterialType,
        "PLW1415",
      ),
      contents: [content],
      tree: createSingleItemTree(content),
      type: InteriorType.CASE,
      upperPaddingAlignment: paddingAlignment,
    };
    return content;
  }

  private createImportContent(
    caseDefinition: SunlabCaseDefinition,
    padding: PaddingType,
    interiorHeightMm: number,
  ): ContentItem {
    const keyboard =
      caseDefinition.Keyboard || caseDefinition.KeyboardBrandAndModel;
    return {
      count: 1,
      depth: caseDefinition.Depth,
      forceOrientation: false,
      height: interiorHeightMm,
      padding: createEqualPaddingMap(padding.strength),
      paddingCubes: [],
      paddingExtraForBottom: 0,
      title: keyboard ? keyboard.replace(/, BxTxH.*\)/, ")") : "Innenraum",
      width: caseDefinition.Width,
    };
  }

  private profileToleranceIncrement(
    closingSection: SunlabCaseDefinition["Sections"][number],
  ): number {
    if (!["9,5", "9,5 inv", "STW6103"].includes(closingSection.Section)) {
      return 0;
    }
    this.success(`Schließprofil ist '9,5', '9,5 inv' oder '9,5 hybrid'
                          (STW6103), daher wird die Toleranz um einen Millimeter erhöht.`);
    return 1;
  }

  private applyRollerBoardOptions(
    serializedResult: FlightCaseSettings,
    caseDefinition: SunlabCaseDefinition,
  ): void {
    const detectedRollersName = this.getRollers(caseDefinition);
    const hasRollerBoard = this.isRollerBoardName(detectedRollersName);
    const rollerIsCreeperType =
      serializedResult.exteriorOptions.rollers.rollerType ===
      ROLLER_TYPE.CREEPER;

    if (rollerIsCreeperType && detectedRollersName !== "Keine") {
      const creeperRoller = serializedResult.exteriorOptions
        .rollers as unknown as CreeperRollerType;
      this.addRollerBoardOption(
        serializedResult,
        hasRollerBoard ? detectedRollersName : "Rollbrett gebremst",
        creeperRoller,
      );
      return;
    }

    if (rollerIsCreeperType) {
      serializedResult.exteriorOptions.rollers = this.findNoRoller();
      return;
    }

    if (hasRollerBoard) {
      this.addDetectedRollerBoardOption(
        serializedResult,
        caseDefinition,
        detectedRollersName,
      );
    }
  }

  private addRollerBoardOption(
    serializedResult: FlightCaseSettings,
    rollerBoardName: string,
    selectedRoller: CreeperRollerType,
  ): void {
    const rollerBoardOption = this.createRollerBoardFromPresetOrFallback(
      rollerBoardName,
      selectedRoller,
      selectedRoller,
    );
    if (rollerBoardOption) {
      serializedResult.variableOptions.push(rollerBoardOption);
    }
    serializedResult.exteriorOptions.rollers = this.findNoRoller();
  }

  private addDetectedRollerBoardOption(
    serializedResult: FlightCaseSettings,
    caseDefinition: SunlabCaseDefinition,
    detectedRollersName: string,
  ): void {
    const selectedRoller = this.findCreeperRollerForBoard(caseDefinition);
    if (selectedRoller) {
      this.addRollerBoardOption(
        serializedResult,
        detectedRollersName,
        selectedRoller,
      );
      return;
    }
    this.error(
      `RollerType "${detectedRollersName}" erkannt, aber keine CREEPER-Rollen in der Datenbank gefunden.`,
    );
  }

  private applyRequiredPresets(
    serializedResult: FlightCaseSettings,
    requiredPresets: RequiredPreset[],
  ): void {
    const targetProductType = this.normalizeProductType(
      String(serializedResult.type),
    );
    for (const requiredPreset of requiredPresets) {
      if (
        requiredPreset.possibleTypes.includes(VariableOptionType.ROLLER_BOARD)
      ) {
        continue;
      }
      const matchingPresets = filter(this.presets, (preset) =>
        this.presetMatchesRequiredPreset(
          preset,
          requiredPreset,
          targetProductType,
        ),
      );
      this.addRequiredPresetOptions(
        serializedResult,
        requiredPreset,
        matchingPresets,
      );
    }
  }

  private normalizeProductType(t: string | undefined): string {
    return (typeof t === "string" ? t.trim().toLowerCase() : "") || "";
  }

  private presetMatchesRequiredPreset(
    preset: LTCasePreset,
    requiredPreset: RequiredPreset,
    targetProductType: string,
  ): boolean {
    const presetProductType = this.normalizeProductType(preset.productType);
    if (presetProductType !== "" && presetProductType !== targetProductType) {
      return false;
    }
    const data = preset.data as
      | {
          tags?: string[];
          partSelection?: { name: string };
        }
      | undefined;
    const presetTags = preset.tags ?? data?.tags ?? [];
    if (!presetTags.includes(requiredPreset.configuration)) {
      return false;
    }
    return (
      this.canonicalOptionName(data?.partSelection?.name ?? "") ===
      this.canonicalOptionName(requiredPreset.partSelection)
    );
  }

  private canonicalOptionName(name: string): string {
    return (
      (this.optionDB.getAnyByName(name) as { name?: string } | undefined)
        ?.name ?? name
    );
  }

  private addRequiredPresetOptions(
    serializedResult: FlightCaseSettings,
    requiredPreset: RequiredPreset,
    matchingPresets: LTCasePreset[],
  ): void {
    if (matchingPresets.length === 0) {
      const option = this.createOptionFromImport(requiredPreset);
      if (option) serializedResult.variableOptions.push(option);
      return;
    }

    for (const matchingPreset of matchingPresets) {
      serializedResult.variableOptions.push(
        this.createOptionFromPreset(matchingPreset, requiredPreset),
      );
    }
  }

  private createOptionFromPreset(
    matchingPreset: LTCasePreset,
    requiredPreset: RequiredPreset,
  ): VariableOptions {
    const presetData = matchingPreset.data as VariableOptions;
    const presetTags =
      matchingPreset.tags ?? (presetData as { tags?: readonly string[] }).tags;
    const normalizedTags = presetTags ? [...presetTags] : undefined;
    return {
      ...presetData,
      ...(normalizedTags && normalizedTags.length > 0
        ? { tags: normalizedTags }
        : {}),
      partSelection: this.optionDB.getAnyByName(requiredPreset.partSelection),
      ...(matchingPreset.id ? { preset: matchingPreset.id } : {}),
      uuid: crypto.randomUUID(),
    } as VariableOptions;
  }

  private createOptionFromImport(
    requiredPreset: RequiredPreset,
  ): VariableOptions | undefined {
    const gen = this.getVariableOptionForRequiredPreset(requiredPreset);
    const optionFromImport = gen.next().value as VariableOptions | undefined;
    if (!optionFromImport) return undefined;

    return {
      ...optionFromImport,
      partSelection: this.optionDB.getAnyByName(requiredPreset.partSelection),
      tags: [requiredPreset.configuration],
      uuid: crypto.randomUUID(),
    } as VariableOptions;
  }

  private applyRack19Options(
    serializedResult: FlightCaseSettings,
    caseDefinition: SunlabCaseDefinition,
    caseType: SunlabsCaseType,
    caseVariant: SunlabsCaseVariant,
  ): void {
    if (serializedResult.interiorOptions.type !== InteriorType.RACK19) {
      return;
    }

    serializedResult.interiorOptions.width = 464;
    const rackRailType = this.applyRackRailVariantOptions(
      serializedResult,
      caseVariant,
    );
    serializedResult.interiorOptions.rackRails =
      this.optionDB.getOrThrow<RackRailType>(LTCaseUserOption.RackRailType, {
        symbol: rackRailType,
      });

    if (caseType === SunlabsCaseType.RACK_DOUBLE_DOOR) {
      serializedResult.interiorOptions.rackRailDisabled[Sides.BACK] =
        !caseDefinition.OptionAdditionalBackRackRail;
    }
  }

  private applyRackRailVariantOptions(
    serializedResult: FlightCaseSettings,
    caseVariant: SunlabsCaseVariant,
  ): string {
    switch (caseVariant) {
      case SunlabsCaseVariant.ECONOMY:
        return this.applyEconomyRackRailOptions(serializedResult);
      case SunlabsCaseVariant.LIVE:
        this.setRackUnderlayStrength(serializedResult, 9);
        return "RAZ1";
      case SunlabsCaseVariant.PROFESSIONAL:
      case SunlabsCaseVariant.HEAVY:
        this.setRackUnderlayStrength(serializedResult, 6.5);
        return "RAZ2";
      default:
        throw new Error(`unexpected case variant ${caseVariant}`);
    }
  }

  private applyEconomyRackRailOptions(
    serializedResult: FlightCaseSettings,
  ): string {
    if (this.rackHasLockCutouts(serializedResult)) {
      this.setRackUnderlayStrength(serializedResult, 6.5);
      return "Eco1";
    }

    serializedResult.interiorOptions.width = 465;
    this.setRackUnderlayStrength(serializedResult, 0);
    return serializedResult.type === LTProducts.DoubleDoor ||
      serializedResult.type === LTProducts.SingleDoor
      ? "Eco2"
      : "RAZ1";
  }

  private setRackUnderlayStrength(
    serializedResult: FlightCaseSettings,
    underlayStrength: number,
  ): void {
    (
      serializedResult.interiorOptions as { underlayStrength: number }
    ).underlayStrength = underlayStrength;
  }

  private rackHasLockCutouts(serializedResult: FlightCaseSettings): boolean {
    return serializedResult.variableOptions.some(
      (o) =>
        o.variableOptionType === VariableOptionType.LOCK_SET &&
        ((o.partSelection as { cut?: number })?.cut ?? 0) > 0,
    );
  }

  private attachImportOptionsToRequiredPresets(
    requiredPresets: RequiredPreset[],
  ): RequiredPreset[] {
    return requiredPresets.map((req) => {
      const gen = this.getVariableOptionForRequiredPreset(req);
      const first = gen.next().value as VariableOptions | undefined;
      return {
        ...req,
        ...(first && { variableOptionFromImport: first }),
      };
    });
  }

  private reportContainsOnlyInfo(): boolean {
    return (
      find(
        this.report,
        (entry: ReportEntry) => entry.type !== ReportEntryType.INFO,
      ) === undefined
    );
  }

  public loadPadding(data: SunlabCaseDefinition) {
    let padding: PaddingType | null;
    let paddingTop: PaddingType | null;
    const noPadding = this.optionDB.getByName<PaddingType>(
      LTCaseUserOption.PaddingType,
      "nopad",
    );

    if (data.FoamBody != null) {
      padding = this.optionDB.getByName<PaddingType>(
        LTCaseUserOption.PaddingType,
        data.FoamBody,
      );
      if (padding == null) {
        this.error(`Konnte Schaumstoff ${data.FoamBody} für Korpus nicht finden.
                    Schaumstoff SST1042 als Ersatz verwendet.`);
        padding = this.optionDB.getByName<PaddingType>(
          LTCaseUserOption.PaddingType,
          "SST1042",
        );
      }
    } else {
      padding = noPadding;
    }

    if (data.FoamCap != null) {
      paddingTop = this.optionDB.getByName<PaddingType>(
        LTCaseUserOption.PaddingType,
        data.FoamCap,
      );
      if (paddingTop == null) {
        this.error(
          // tslint:disable-next-line:max-line-length
          `Konnte Schaumstoff ${data.FoamCap} für Deckel nicht finden. Schaumstoff SST1042 als Ersatz verwendet.`,
        );
        paddingTop = this.optionDB.getByName<PaddingType>(
          LTCaseUserOption.PaddingType,
          "SST1056",
        );
      }
    } else {
      paddingTop = noPadding;
    }
    return {
      padding,
      paddingAlignment: true,
      paddingTop,
      tolerance: paddingTop.strength === 0 ? 2 : -3,
    };
  }

  public loadAngleSections(data: SunlabCaseDefinition): string {
    // sanity checks on megacase data
    if (
      data.EdgeProtectionCap === "STW1378" &&
      data.EdgeProtectionBody === "STW1377"
    ) {
      return "STW1377/STW1378";
    }
    if (
      data.EdgeProtectionCap === "STW1377" &&
      data.EdgeProtectionBody === "STW1378"
    ) {
      return "STW1378/STW1377";
    }
    // CaseConf omits EdgeProtectionBody for some case types; the cap value is
    // the sane fallback, since the two are expected to match anyway.
    if (data.EdgeProtectionBody == null) {
      return data.EdgeProtectionCap;
    }
    if (data.EdgeProtectionBody !== data.EdgeProtectionCap) {
      this.warn(
        `Kantenschutz für Korpus(${data.EdgeProtectionBody}) und ` +
          `Deckel (${data.EdgeProtectionCap}) sind nicht gleich.`,
      );
    }
    return data.EdgeProtectionBody;
  }

  public *transformQuoteWheelsQuote(
    data: SunlabCaseDefinition,
  ): IterableIterator<VariableOption> {
    for (const wheel of data.Wheels ?? []) {
      const name = wheel.Wheel;
      const config = wheel.WheelConfiguration;
      // const [type, subtype] = (config.split("_"));
      const preset = find(this.presets, { name: config });
      if (preset) {
        const presetData = preset.data as VariableOptions;
        const presetTags =
          preset.tags ?? (presetData as { tags?: readonly string[] }).tags;
        const normalizedTags = presetTags ? [...presetTags] : undefined;
        const fromPreset = {
          ...presetData,
          ...(normalizedTags && normalizedTags.length > 0
            ? { tags: normalizedTags }
            : {}),
          partSelection: this.optionDB.getAnyByName(name),
          uuid: crypto.randomUUID(),
        } as VariableOptions;
        yield fromPreset;
      } else {
        this.warn(`Preset not found for wheel config "${config}", skipping.`);
      }
    }
  }

  private sunlabsWheelSide(subtype: string): Sides | undefined {
    switch (subtype) {
      case "Standard":
      case "WithPulloutHandle":
      case "TwoRightBody":
      case "WithTrolleyFunction":
        return Sides.RIGHT;
      case "TwoLeftBody":
        return Sides.LEFT;
      default:
        return undefined;
    }
  }

  private resolveSunlabsExteriorWheelImport(
    name: string,
    config: string,
    subtype: string | undefined,
  ): { kind: "exterior"; baseName: string; side: Sides } | undefined {
    let resolvedName = name;
    if (name === "ROL3044") {
      resolvedName = "AH 37600S";
      this.success("Rad '3044' ausgetauscht gegen Rad 'AH37600S'.");
    }

    const side = this.sunlabsWheelSide(subtype ?? "");
    if (side === undefined) {
      this.error(`Rad Untertyp ${config} für Rad ${resolvedName} unbekannt.`);
      return undefined;
    }
    return { kind: "exterior", baseName: resolvedName, side };
  }

  private resolveSunlabsWheelboardWheelImport(
    name: string,
    config: string,
  ): { kind: "rollerBoard"; name: string } | undefined {
    if (config === "WheelboardWheelNonBraked_FourBottomBody") {
      return { kind: "rollerBoard", name: "Rollbrett ungebremst" };
    }
    if (config === "WheelboardWheelBraked_TwoBottomBody") {
      return { kind: "rollerBoard", name: "Rollbrett gebremst" };
    }
    if (config !== "WheelboardWheelNonBraked_TwoBottomBody") {
      this.error(`Rollbrett Untertyp ${config} für Rad ${name} unbekannt.`);
    }

    const supportedWheelboardCasters = new Set([
      "3360UFR100P62",
      "3377UFR100P62",
    ]);
    if (!supportedWheelboardCasters.has(name)) {
      this.warn(`Lenkrolle ${name} für Rollbrett wird nicht unterstützt.`);
    }
    return undefined;
  }

  public getRollerImportSelection(
    data: SunlabCaseDefinition,
  ):
    | { kind: "none" }
    | { kind: "rollerBoard"; name: string }
    | { kind: "exterior"; baseName: string; side: Sides } {
    for (const wheel of data.Wheels ?? []) {
      const config = wheel.WheelConfiguration;
      const [type, subtype] = config.split("_");

      switch (type) {
        case "Wheel": {
          const exteriorSelection = this.resolveSunlabsExteriorWheelImport(
            wheel.Wheel,
            config,
            subtype,
          );
          if (exteriorSelection) {
            return exteriorSelection;
          }
          break;
        }
        case "WheelboardBraked":
        case "WheelboardNonBraked":
          break;
        case "WheelboardWheelBraked":
        case "WheelboardWheelNonBraked": {
          const rollerBoardSelection = this.resolveSunlabsWheelboardWheelImport(
            wheel.Wheel,
            config,
          );
          if (rollerBoardSelection) {
            return rollerBoardSelection;
          }
          break;
        }
        default:
      }
    }
    return { kind: "none" };
  }

  /** @deprecated Use {@link getRollerImportSelection} — name suffix no longer used. */
  public getRollers(data: SunlabCaseDefinition): string {
    const selection = this.getRollerImportSelection(data);
    if (selection.kind === "none") return "Keine";
    if (selection.kind === "rollerBoard") return selection.name;
    return selection.baseName;
  }

  // FIXME why is there no yield?
  public *loadConnectors(
    data: SunlabCaseDefinition,
    _caseType: SunlabsCaseType,
    _variant: SunlabsCaseVariant,
  ) {
    const connectors = data.Hinges.map((h) => ({
      configuration: h.HingeConfiguration,
      partSelection: h.Hinge,
    }));
    connectors.push({
      configuration: `${SunlabsConnectorType.Lock}_${data.FastenerConfiguration}`,
      partSelection: data.Fastener,
    });
  }

  private error(message: string) {
    this.report.push({ message, type: ReportEntryType.ERROR });
  }

  private success(message: string) {
    this.report.push({ message, type: ReportEntryType.INFO });
  }

  private findRollerBoardPreset(
    rollerBoardName: string,
  ): LTCasePreset | undefined {
    const tag =
      rollerBoardName === "Rollbrett gebremst"
        ? "WheelboardWheelBraked"
        : "WheelboardWheelNonBraked";
    return this.presets.find((p) => {
      if (p.category !== VariableOptionType.ROLLER_BOARD || p.deleted) {
        return false;
      }
      const presetTags =
        p.tags ??
        (p.data as { tags?: readonly string[] } | undefined)?.tags ??
        [];
      return presetTags.includes(tag);
    });
  }

  private findCreeperRollerForBoard(
    caseDefinition: SunlabCaseDefinition,
  ): CreeperRollerType | undefined {
    const allRollers = this.optionDB.getAllOfKind<RollerBoardTypes>(
      LTCaseUserOption.RollerType,
    );
    const creeperRollers = allRollers.filter(
      (r) => r.rollerType === ROLLER_TYPE.CREEPER,
    );
    if (creeperRollers.length === 0) return undefined;

    const wheelboardWheel = caseDefinition.Wheels?.find(
      (w) =>
        w.WheelConfiguration.startsWith("WheelboardWheelBraked") ||
        w.WheelConfiguration.startsWith("WheelboardWheelNonBraked"),
    );
    const wheelPartName = wheelboardWheel?.Wheel;
    if (wheelPartName) {
      const exact = creeperRollers.find(
        (r) =>
          r.name?.replaceAll(/\s/g, "") === wheelPartName.replaceAll(/\s/g, ""),
      );
      if (exact) return exact;
    }
    return creeperRollers[0] as CreeperRollerType;
  }

  private resolveRollerFromPreset(
    presetRoller: unknown,
    fallback: CreeperRollerType,
  ): CreeperRollerType {
    if (!presetRoller || typeof presetRoller !== "object") return fallback;
    const roller = presetRoller as Record<string, unknown>;
    const rollerName = typeof roller.name === "string" ? roller.name : "";
    if (!rollerName) return fallback;

    const allRollers = this.optionDB.getAllOfKind<RollerBoardTypes>(
      LTCaseUserOption.RollerType,
    );
    const match = allRollers.find(
      (r) =>
        r.rollerType === ROLLER_TYPE.CREEPER &&
        r.name?.replaceAll(/\s/g, "") === rollerName.replaceAll(/\s/g, ""),
    );
    if (match) return match as CreeperRollerType;

    if ((roller as { board?: unknown }).board) {
      return presetRoller as CreeperRollerType;
    }
    return fallback;
  }

  private createRollerBoardFromPresetOrFallback(
    rollerBoardName: string,
    fallbackRollerA: CreeperRollerType,
    fallbackRollerB: CreeperRollerType,
  ): VariableOptions | undefined {
    const preset = this.findRollerBoardPreset(rollerBoardName);
    if (preset) {
      const presetData = preset.data as Record<string, unknown>;
      const presetTags =
        preset.tags ?? (presetData as { tags?: readonly string[] }).tags;
      const normalizedTags = presetTags ? [...presetTags] : undefined;
      const resolvedRollerA = this.resolveRollerFromPreset(
        presetData.rollerA,
        fallbackRollerA,
      );
      const resolvedRollerB = this.resolveRollerFromPreset(
        presetData.rollerB,
        fallbackRollerB,
      );
      const option = {
        ...presetData,
        rollerA: resolvedRollerA,
        rollerB: resolvedRollerB,
        variableOptionType: VariableOptionType.ROLLER_BOARD,
        ...(normalizedTags && normalizedTags.length > 0
          ? { tags: normalizedTags }
          : {}),
        ...(preset.id ? { preset: preset.id } : {}),
        uuid: crypto.randomUUID(),
      } as unknown as VariableOptions;
      return option;
    }

    return createRollerBoard(
      this.optionDB,
      fallbackRollerA,
      fallbackRollerB,
    ) as unknown as VariableOptions;
  }

  /**
   * Sunlabs only requires one of LCornerBody/LCornerCap for import validation.
   * When LCornerBody is absent, LCornerCap is often a schema placeholder — ignore it.
   */
  private resolveImportedCornerBraces(caseDefinition: SunlabCaseDefinition): {
    cornerBraces: CornerBraceType;
    cornerBracesCover: CornerBraceType;
  } {
    const lCornerBody = caseDefinition.LCornerBody;
    const lCornerCap = caseDefinition.LCornerCap;
    const bodyFieldPresent = lCornerBody != null && lCornerBody.trim() !== "";

    return {
      cornerBraces: this.resolveExteriorCornerBrace(
        bodyFieldPresent ? lCornerBody : "Keine",
      ),
      cornerBracesCover: this.resolveExteriorCornerBrace(
        bodyFieldPresent ? lCornerCap || "Keine" : "Keine",
      ),
    };
  }

  private resolveExteriorCornerBrace(name: string): CornerBraceType {
    return this.optionDB.getByName<CornerBraceType>(
      LTCaseUserOption.CornerBraceType,
      name,
    );
  }

  private findNoRoller(): RollerBoardTypes {
    return this.optionDB.getByName(LTCaseUserOption.RollerType, "Keine");
  }

  private warn(message: string) {
    this.report.push({ message, type: ReportEntryType.WARNING });
  }
}
